import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, } from 'rxjs';
import { catchError, filter, shareReplay, tap } from 'rxjs/operators';
import { Cacheable } from 'ts-cacheable';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private _cacheData$: BehaviorSubject<any> = new BehaviorSubject(null);
  private cacheRinnovi$: Observable<any[]>;

  private cachePagamenti$: Observable<any[]>;
  private cacheGruppo$: Observable<any[]>;

  private httpHeadersDefault: HttpHeaders;

  constructor(private http: HttpClient) {
    this.httpHeadersDefault = new HttpHeaders().set('Content-type', 'application/json');
  }

  getData(force : boolean = false): Observable<any> {
    if (force || !this._cacheData$.value) {
      this.requestData().subscribe((data) => {
        this._cacheData$.next(data);
      });
    }

    return this._cacheData$.asObservable().pipe(filter(data => data != undefined && data != null));
  }

  get rinnovi(): Observable<any[]> {
    if (!this.cacheRinnovi$) {
      this.cacheRinnovi$ = this.requestRinnovi().pipe(
        shareReplay(1)
      );
    }

    return this.cacheRinnovi$;
  }

  get pagamenti(): Observable<any[]> {
    if (!this.cachePagamenti$) {
      this.cachePagamenti$ = this.requestPagamenti().pipe(
        shareReplay(1)
      );
    }

    return this.cachePagamenti$;
  }

  get gruppo(): Observable<any> {
    if (!this.cacheGruppo$) {
      this.cacheGruppo$ = this.requestGruppo().pipe(
        shareReplay(1)
      );
    }

    return this.cacheGruppo$;
  }

  cleanGruppo() {
    this.cacheGruppo$ = undefined;
  }

  private requestData(): Observable<any> {
    const url: string = '/api/dolomiti/get-data';
    return this.http.get<any>(url).pipe(catchError((err) => {

      if(err.error.status == 404) {

        return of({});
      }
    }));
  }

  private requestRinnovi(): Observable<any[]> {
    const url: string = '/api/dolomiti/get-rinnovi';
    return this.http.get<any[]>(url);
  }

  private requestPagamenti(): Observable<any[]> {
    const url: string = '/api/dolomiti/get-pagamenti';
    return this.http.get<any[]>(url);
  }

  private requestGruppo(): Observable<any> {
    const url: string = '/api/dolomiti/get-gruppo';
    return this.http.get<any>(url);
  }

  @Cacheable()
  getBeneficiariPagamento(idPagamento: number): Observable<any[]> {
    let params = new HttpParams().set('idPagamento', idPagamento.toString());

    return this.http.get<any[]>('/api/dolomiti/get-beneficiari', { params: params });
  }

  @Cacheable()
  getNazioni(): Observable<any[]> {
    return this.http.get<any[]>('/api/dolomiti/get-nazioni');
  }

  @Cacheable()
  getAssociazioni(): Observable<any[]> {
    return this.http.get<any[]>('/api/dolomiti/get-associazioni');
  }
  
  getSocioGestito(idSocioGestito: string): Observable<any[]> {
    let params = new HttpParams().set('idSocioGestito', idSocioGestito);
    return this.http.get<any[]>('/api/dolomiti/get-data-socio-gestito', { params: params });
  }
  
  getAmicoFedele(idSocio: string): Observable<any[]> {
    let params = new HttpParams().set('id', idSocio);
    return this.http.get<any[]>('/api/dolomiti/get-data-amico-fedele', { params: params });
  }
  
  setAssociazione(socio: any): Observable<any> {
    const options = { headers: this.httpHeadersDefault };
    return this.http.post<any>('/api/dolomiti/set-associazione', socio, options);
  }

  setContatti(socio: any): Observable<any> {
    const options = { headers: this.httpHeadersDefault};
    return this.http.post<any>('/api/dolomiti/set-contatti', socio, options);
  }

  setIndirizzo(socio: any): Observable<any> {
    const options = { headers: this.httpHeadersDefault };
    return this.http.post<any>('/api/dolomiti/set-indirizzo', socio, options);
  }

  deleteAssociazioneGruppo(idSocio: number, tipo: number): Observable<any> {
    let params = new HttpParams().set('idSocioToRemove', idSocio.toString())
      .set('tipo', tipo.toString());

    return this.http.get<any>('/api/dolomiti/delete-socio-gruppo', { params: params }).pipe(tap(() => this.cleanGruppo()));
  }

  assignSocioGruppo(idSocio: number, idTessera: number): Observable<any> {
    let params = new HttpParams().set('idSocioToAdd', idSocio.toString())
      .set('idTessera', idTessera.toString());

    return this.http.get<any>('/api/dolomiti/add-socio-gruppo', { params: params }).pipe(tap(() => this.cleanGruppo()));
  }

  assignAmicoFedele(idSocio: number, chip: string): Observable<any> {
    let params = new HttpParams().set('idSocioToAdd', idSocio.toString())
      .set('chip', chip);
    return this.http.get<any>('/api/dolomiti/add-amicofedele-gruppo', { params: params }).pipe(tap(() => this.cleanGruppo()));
  }

  setSocioGestito(socio: any): Observable<any> {
    const options = { headers: this.httpHeadersDefault };
    return this.http.post<any>('/api/dolomiti/set-dati-socio-gestito', socio, options).pipe(tap(() => this.cleanGruppo()));
  }

  setAmicoFedele(amicoFedele: any): Observable<any> {
    const options = { headers: this.httpHeadersDefault };
    return this.http.post<any>('/api/dolomiti/set-dati-amicofedele', amicoFedele, options).pipe(tap(() => this.cleanGruppo()));
  }
  
  addSocioGruppo(socio: any): Observable<any> {
    const options = { headers: this.httpHeadersDefault };
    return this.http.post<any>('/api/dolomiti/add-socio-gruppo', socio, options).pipe(tap(() => this.cleanGruppo()));
  }

  addAmicoFedele(amicoFedele: any): Observable<any> {
    const options = { headers: this.httpHeadersDefault };
    return this.http.post<any>('/api/dolomiti/add-amicofedele', amicoFedele, options).pipe(tap(() => this.cleanGruppo()));
  }

  getInfoPagamento(): Observable<any> {
    return this.http.get<any>('/api/dolomiti-pagamenti/get-info-pagamento');
  }

  addNuovoPagamento(pagamento: any): Observable<any> {
    const options = { headers: this.httpHeadersDefault };
    return this.http.post<any>('/api/dolomiti-pagamenti/add-nuovo-pagamento', pagamento, options);
  }
  
  getTempPagamento(): Observable<any> {
    return this.http.get<any>('/api/dolomiti-pagamenti/get-temp-pagamento');
  }
  
  changePassword(oldPassword: string, newPassword: string) {

    const formData: FormData = new FormData();

    formData.append("oldPassword", oldPassword);
    formData.append("newPassword", newPassword);
    const options = { headers: this.httpHeadersDefault };
    return this.http.post<any>(`${environment.authUrl}/api/changePassword`, formData);
  }

  getRicevutaPagamento(id): Observable<any> {
    let params = new HttpParams().set('idPagamento', id.toString());
    const options = { params: params, responseType: 'blob' as 'json' };
    return this.http.get<any>(`${environment.adminUrl}/api/pdf/getRicevuta`, options);
  }

  getLetteraRinnovoAdesione(dataRinnovo: string): Observable<any> {
    let params = new HttpParams().set('dataRinnovo', dataRinnovo);
    const options = { params: params, responseType: 'blob' as 'json' };
    return this.http.get<any>(`${environment.adminUrl}/api/pdf/getLetteraSocio`, options);
  }

  // customerSecredId: string
  confirmPayment(paymentId: string, paymentIntentId: string ) : Observable<any> {
    const formData: FormData = new FormData();

    formData.append("paymentId", paymentId);
    formData.append("paymentIntentId", paymentIntentId);
    //formData.append("customerSecredId", customerSecredId);
    return this.http.post("/api/dolomiti-pagamenti/confirm-pagamento", formData)
  }

}
